import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Users, Activity, Euro, TrendingUp, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { CreatorSheet } from "@/components/admin/CreatorSheet";
import { FaqManager } from "@/components/admin/FaqManager";
import { OrdersTable } from "@/components/admin/OrdersTable";
import { DiscountRequestsTable } from "@/components/admin/DiscountRequestsTable";
import { CrmTable } from "@/components/admin/CrmTable";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { LOGO_URL, STATUS_ORDER, isEU } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";

const FIELD = "bg-transparent border border-aura-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-white/40 outline-none transition-colors";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-aura-border bg-aura-surface p-5 hover:border-aura-borderhover transition-colors">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.1em] text-zinc-500">{label}</span>
        <Icon size={16} className="text-zinc-600" />
      </div>
      <div className="text-3xl font-display font-bold text-white">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("creators");
  const [stats, setStats] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [filters, setFilters] = useState({ status: "", country: "", platform: "", min_followers: "", min_sales: "", min_commissions: "", search: "" });

  const { logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const fetchStats = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const fetchCreators = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
    try { const { data } = await api.get("/admin/creators", { params }); setCreators(data); }
    catch { toast.error("Error"); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchCreators(); }, [fetchCreators]);

  const doLogout = async () => { await logout(); navigate("/"); };
  const openCreator = (c) => { setSelected(c); setSheetOpen(true); };
  const onSaved = (updated) => { setCreators((arr) => arr.map((c) => (c.id === updated.id ? updated : c))); setSelected(updated); fetchStats(); };
  const onDeleted = (id) => { setCreators((arr) => arr.filter((c) => c.id !== id)); setSheetOpen(false); fetchStats(); };

  const countries = useMemo(() => stats ? Object.keys(stats.by_country || {}).sort() : [], [stats]);
  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const clearFilters = () => setFilters({ status: "", country: "", platform: "", min_followers: "", min_sales: "", min_commissions: "", search: "" });
  const hasFilters = Object.values(filters).some((v) => v !== "");

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-2xl border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="AURA+" className="h-9 w-auto mix-blend-screen opacity-95" />
            <span className="hidden sm:inline text-xs uppercase tracking-[0.2em] text-zinc-500 border-s border-white/10 ps-4">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button data-testid="admin-logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"><LogOut size={16} /> {t("admin.logout")}</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-5 md:px-8 py-8 md:py-10" data-testid="admin-dashboard">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-8">{t("admin.title")}</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard testid="stat-total" icon={Users} label={t("admin.statTotal")} value={stats?.total_creators ?? "—"} />
          <StatCard testid="stat-active" icon={Activity} label={t("admin.statActive")} value={stats?.active_creators ?? "—"} />
          <StatCard testid="stat-sales" icon={Euro} label={t("admin.statSales")} value={stats ? `€${stats.total_sales.toLocaleString()}` : "—"} />
          <StatCard testid="stat-commissions" icon={TrendingUp} label={t("admin.statCommissions")} value={stats ? `€${stats.total_commissions.toLocaleString()}` : "—"} />
        </div>

        <div className="flex items-center gap-2 border-b border-white/10 mb-6">
          {[{ k: "creators", l: t("admin.tabCreators") }, { k: "crm", l: t("crm.tab") }, { k: "orders", l: t("orders.tab") }, { k: "codes", l: t("dcodes.tab") }, { k: "faq", l: t("admin.tabFaq") }].map((tb) => (
            <button key={tb.k} data-testid={`admin-tab-${tb.k}`} onClick={() => setTab(tb.k)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === tb.k ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              {tb.l}
            </button>
          ))}
        </div>

        {tab === "faq" ? <FaqManager /> : tab === "orders" ? <OrdersTable /> : tab === "codes" ? <DiscountRequestsTable /> : tab === "crm" ? <CrmTable /> : (
          <>
            <div className="rounded-xl border border-aura-border bg-aura-surface p-4 mb-6" data-testid="admin-filters">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="col-span-2 md:col-span-1 relative">
                  <Search size={15} className="absolute start-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input data-testid="filter-search" className={`${FIELD} w-full ps-9`} placeholder={t("admin.search")} value={filters.search} onChange={setF("search")} />
                </div>
                <select data-testid="filter-status" className={FIELD} value={filters.status} onChange={setF("status")}>
                  <option value="" className="bg-[#0a0a0a]">{t("admin.allStatuses")}</option>
                  {STATUS_ORDER.map((s) => <option key={s} value={s} className="bg-[#0a0a0a]">{t(`status.${s}`)}</option>)}
                </select>
                <select data-testid="filter-country" className={FIELD} value={filters.country} onChange={setF("country")}>
                  <option value="" className="bg-[#0a0a0a]">{t("admin.allCountries")}</option>
                  {countries.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
                </select>
                <select data-testid="filter-platform" className={FIELD} value={filters.platform} onChange={setF("platform")}>
                  <option value="" className="bg-[#0a0a0a]">{t("admin.allPlatforms")}</option>
                  <option value="instagram" className="bg-[#0a0a0a]">Instagram</option>
                  <option value="tiktok" className="bg-[#0a0a0a]">TikTok</option>
                  <option value="youtube" className="bg-[#0a0a0a]">YouTube</option>
                </select>
                <input data-testid="filter-followers" type="number" className={FIELD} placeholder={t("admin.minFollowers")} value={filters.min_followers} onChange={setF("min_followers")} />
                <input data-testid="filter-sales" type="number" className={FIELD} placeholder={t("admin.minSales")} value={filters.min_sales} onChange={setF("min_sales")} />
              </div>
              {hasFilters && (
                <button data-testid="filter-clear" onClick={clearFilters} className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors">
                  <X size={13} /> {t("admin.clearFilters")}
                </button>
              )}
            </div>

            <div className="rounded-xl border border-aura-border bg-aura-surface overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="creators-table">
                  <thead>
                    <tr className="border-b border-white/10 text-start text-xs uppercase tracking-wider text-zinc-500">
                      <th className="px-4 py-3 font-medium text-start">{t("admin.colCreator")}</th>
                      <th className="px-4 py-3 font-medium text-start">{t("admin.colCountry")}</th>
                      <th className="px-4 py-3 font-medium text-start">{t("admin.colFollowers")}</th>
                      <th className="px-4 py-3 font-medium text-start">{t("admin.colSales")}</th>
                      <th className="px-4 py-3 font-medium text-start">{t("admin.colCommissions")}</th>
                      <th className="px-4 py-3 font-medium text-start">{t("admin.colStatus")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-500"><Loader2 className="animate-spin inline" /></td></tr>
                    ) : creators.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-500">{t("admin.empty")}</td></tr>
                    ) : creators.map((c) => (
                      <tr key={c.id} data-testid={`creator-row-${c.id}`} onClick={() => openCreator(c)} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors">
                        <td className="px-4 py-4">
                          <div className="font-medium text-white flex items-center gap-2">
                            {c.first_name} {c.last_name}
                            {c.founding_member && <span className="text-amber-400 text-[10px] border border-amber-500/40 rounded px-1.5 py-0.5">FM</span>}
                          </div>
                          <div className="text-xs text-zinc-500">{c.email}</div>
                        </td>
                        <td className="px-4 py-4">
                          {c.country ? (
                            <span className="inline-flex items-center gap-1.5 text-zinc-300" title={isEU(c.country) ? "UE" : "Extra-UE — dogana"}>
                              <span className={`h-1.5 w-1.5 rounded-full ${isEU(c.country) ? "bg-sky-400" : "bg-amber-400"}`} />
                              {c.country}
                            </span>
                          ) : <span className="text-zinc-600">—</span>}
                        </td>
                        <td className="px-4 py-4 text-zinc-300">{(c.followers || 0).toLocaleString()}</td>
                        <td className="px-4 py-4 text-zinc-300">€{(c.sales_generated || 0).toLocaleString()}</td>
                        <td className="px-4 py-4 text-zinc-300">€{(c.commissions || 0).toLocaleString()}</td>
                        <td className="px-4 py-4"><StatusBadge status={c.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-3 text-xs text-zinc-600">{creators.length} {t("admin.creatorsWord")} · {t("admin.rowHint")}</p>
          </>
        )}
      </main>

      <CreatorSheet creator={selected} open={sheetOpen} onOpenChange={setSheetOpen} onSaved={onSaved} onDeleted={onDeleted} />
    </div>
  );
}
